import React, { useRef, useState } from 'react';
import { FiFilm, FiUpload, FiTrash2, FiDownload, FiShuffle, FiMusic, FiImage, FiAlertTriangle, FiZap } from 'react-icons/fi';
import { useFullStoryApi } from './api';
import { useT } from '../../utils/i18n';
import { StepCard, Notice, formatSeconds } from './shared';
import { EffectPicker, effectLabel } from './EffectPicker';

// Moves and transitions for "Mix automatically" — varied, never the same twice in a row.
const AUTO_MOTIONS = ['zoom_in', 'pan_right', 'zoom_out', 'zoom_in_left', 'pan_left', 'zoom_in_right', 'pan_up', 'pan_down'];
const AUTO_TRANSITIONS = ['fade', 'smoothleft', 'dissolve', 'circleopen', 'smoothright', 'fadeblack', 'wipeleft', 'zoomin', 'smoothup'];
const LOGO_POSITIONS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

/** Step 4 — animation per scene, optional logo and music, then the finished video. */
export const VideoStep = ({ story, options, run, readOnly }) => {
  const fullStoryService = useFullStoryApi();
  const t = useT();
  const id = story._id;
  const logoRef = useRef(null);
  const musicRef = useRef(null);
  const rendering = story.video.status === 'rendering';
  const ready = story.video.status === 'ready' && story.video.filename;
  const locked = readOnly || rendering;
  const portrait = story.aspectRatio === '9:16';

  const [effectScene, setEffectScene] = useState(null); // index of the scene whose effect is being chosen

  const setScene = (sceneId, data) => run(() => fullStoryService.updateScene(id, sceneId, data));
  const effectById = (effectId) => (options.effects || []).find((e) => e.id === effectId) || null;
  const effectCount = story.scenes.filter((s) => s.effect && s.effect !== 'none').length;

  const clearEffects = async () => {
    if (!window.confirm(t('fullStory.effect.clearConfirm', { count: effectCount }))) return;
    for (const scene of story.scenes) {
      if (!scene.effect || scene.effect === 'none') continue;
      // eslint-disable-next-line no-await-in-loop
      await fullStoryService.updateScene(id, scene._id, { effect: 'none' }).catch(() => {});
    }
    run(() => fullStoryService.get(id));
  };

  const mixAutomatically = async () => {
    const offset = Math.floor(Math.random() * AUTO_MOTIONS.length);
    for (const [i, scene] of story.scenes.entries()) {
      // eslint-disable-next-line no-await-in-loop
      await fullStoryService.updateScene(id, scene._id, {
        motion: AUTO_MOTIONS[(i + offset) % AUTO_MOTIONS.length],
        transition: AUTO_TRANSITIONS[(i + offset) % AUTO_TRANSITIONS.length],
      }).catch(() => {});
    }
    run(() => fullStoryService.get(id));
  };

  return (
    <StepCard title={t('fullStory.video.title')} description={t('fullStory.video.desc')}>
      {!story.images.approved && <Notice tone="warn">{t('fullStory.video.needImages')}</Notice>}

      {/* Result */}
      {ready && (
        <div className="space-y-3">
          {story.video.outdated && (
            <Notice tone="warn">
              <span className="inline-flex items-center gap-1.5"><FiAlertTriangle size={14} /> {t('fullStory.video.outdated')}</span>
            </Notice>
          )}
          <div className={`mx-auto rounded-xl overflow-hidden bg-black ${portrait ? 'max-w-[340px]' : 'max-w-3xl'}`}>
            <video
              key={story.video.filename}
              controls
              preload="metadata"
              src={fullStoryService.fileUrl(id, story.video.filename)}
              className="w-full h-auto block"
            />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <a href={fullStoryService.fileUrl(id, story.video.filename, { download: true })} className="btn btn-primary flex items-center gap-2">
              <FiDownload size={14} /> {t('fullStory.video.downloadMp4')}
            </a>
            <a href={fullStoryService.voiceoverMp3Url(id)} className="btn btn-ghost flex items-center gap-2">
              <FiDownload size={14} /> {t('fullStory.video.downloadMp3')}
            </a>
          </div>
          <p className="text-center text-xs text-gray-500">
            {t('fullStory.video.madeAt', {
              length: formatSeconds(story.video.durationSec),
              shape: story.aspectRatio,
              time: new Date(story.video.renderedAt).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
            })}
          </p>
        </div>
      )}

      {/* Animation */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h3 className="text-sm font-semibold text-gray-800">{t('fullStory.video.animation')}</h3>
          <div className="flex flex-wrap items-center gap-1.5">
            {!locked && effectCount > 0 && (
              <button onClick={clearEffects} className="btn btn-ghost text-xs py-1.5 px-2.5 flex items-center gap-1.5">
                <FiZap size={12} /> {t('fullStory.effect.clearAll', { count: effectCount })}
              </button>
            )}
            {!locked && story.scenes.length > 1 && (
              <button onClick={mixAutomatically} className="btn btn-ghost text-xs py-1.5 px-2.5 flex items-center gap-1.5">
                <FiShuffle size={12} /> {t('fullStory.video.mixAuto')}
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-2">{t('fullStory.effect.intro', { count: (options.effects || []).length })}</p>
        <ol className="divide-y divide-gray-100 rounded-xl border border-gray-200">
          {story.scenes.map((scene, i) => (
            <li key={scene._id} className="flex flex-wrap sm:flex-nowrap items-center gap-3 px-3 py-2">
              <span className="w-5 text-right text-xs font-semibold text-gray-400 tabular-nums">{i + 1}</span>
              <div className={`flex-shrink-0 rounded-md overflow-hidden bg-gray-100 ${portrait ? 'w-9 h-16' : 'w-24 h-[54px]'}`}>
                {scene.image?.filename && (
                  <img
                    src={fullStoryService.fileUrl(id, scene.image.kind === 'video' ? (scene.image.poster || scene.image.filename) : scene.image.filename)}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}
              </div>
              {scene.image?.kind === 'video' ? (
                // A clip moves on its own — zoom/pan is for pictures only.
                <div className="flex-1 min-w-[150px]">
                  <span className="block text-[11px] text-gray-500">{t('fullStory.video.motion')}</span>
                  <span className="input w-full text-xs py-1.5 flex items-center gap-1.5 text-gray-500 bg-gray-50"><FiFilm size={12} /> {t('fullStory.video.clipMoves')}</span>
                </div>
              ) : (
                <label className="flex-1 min-w-[150px]">
                  <span className="block text-[11px] text-gray-500">{t('fullStory.video.motion')}</span>
                  <select
                    value={scene.motion || 'zoom_in'}
                    disabled={locked}
                    onChange={(e) => setScene(scene._id, { motion: e.target.value })}
                    className="input w-full text-xs py-1.5"
                  >
                    {Object.entries(options.motions).map(([value, label]) => <option key={value} value={value}>{t(`fullStory.motion.${value}`, {}) === `fullStory.motion.${value}` ? label : t(`fullStory.motion.${value}`)}</option>)}
                  </select>
                </label>
              )}
              <label className="flex-1 min-w-[150px]">
                <span className="block text-[11px] text-gray-500">{i === 0 ? t('fullStory.video.opening') : t('fullStory.video.transitionIn')}</span>
                <select
                  value={scene.transition || 'fade'}
                  disabled={locked || i === 0}
                  onChange={(e) => setScene(scene._id, { transition: e.target.value })}
                  className="input w-full text-xs py-1.5 disabled:opacity-50"
                >
                  {i === 0
                    ? <option value={scene.transition || 'fade'}>{t('fullStory.video.startsVideo')}</option>
                    : Object.entries(options.transitions).map(([value, label]) => <option key={value} value={value}>{t(`fullStory.transition.${value}`) === `fullStory.transition.${value}` ? label : t(`fullStory.transition.${value}`)}</option>)}
                </select>
              </label>
              <div className="flex-1 min-w-[150px]">
                <span className="block text-[11px] text-gray-500">{t('fullStory.effect.label')}</span>
                <button
                  type="button"
                  disabled={locked}
                  onClick={() => setEffectScene(i)}
                  title={effectById(scene.effect)?.hint || t('fullStory.effect.choose')}
                  className={`input w-full text-xs py-1.5 flex items-center gap-1.5 text-left disabled:opacity-50 ${scene.effect && scene.effect !== 'none' ? 'border-primary-300 bg-primary-50 text-primary-800' : 'text-gray-600'}`}
                >
                  <FiZap size={12} className="flex-shrink-0" />
                  <span className="truncate">{effectLabel(t, effectById(scene.effect))}</span>
                </button>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {effectScene !== null && story.scenes[effectScene] && (
        <EffectPicker
          story={story}
          scene={story.scenes[effectScene]}
          index={effectScene}
          options={options}
          onApply={(effect) => setScene(story.scenes[effectScene]._id, { effect })}
          onClose={() => setEffectScene(null)}
        />
      )}

      {/* Logo and music */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-gray-200 p-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-800"><FiImage size={14} /> {t('fullStory.video.logo')}</h3>
            {story.options.logo?.filename && (
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={Boolean(story.options.logo.enabled)}
                  disabled={locked}
                  onChange={(e) => run(() => fullStoryService.updateOptions(id, { logo: { enabled: e.target.checked } }))}
                  className="w-4 h-4 rounded accent-primary-500"
                />
                {t('fullStory.video.useInVideo')}
              </label>
            )}
          </div>
          {story.options.logo?.filename ? (
            <div className="flex items-center gap-3">
              <div className="w-24 h-14 rounded-lg bg-[repeating-conic-gradient(#e5e7eb_0%_25%,#fff_0%_50%)] bg-[length:12px_12px] flex items-center justify-center overflow-hidden">
                <img src={fullStoryService.fileUrl(id, story.options.logo.filename)} alt="" className="max-w-full max-h-full object-contain" />
              </div>
              <label className="flex-1">
                <span className="block text-[11px] text-gray-500">{t('fullStory.video.position')}</span>
                <select
                  value={story.options.logo.position || 'top-right'}
                  disabled={locked}
                  onChange={(e) => run(() => fullStoryService.updateOptions(id, { logo: { position: e.target.value } }))}
                  className="input w-full text-xs py-1.5"
                >
                  {LOGO_POSITIONS.map((p) => <option key={p} value={p}>{t(`fullStory.position.${p}`)}</option>)}
                </select>
              </label>
              {!locked && (
                <button onClick={() => run(() => fullStoryService.removeOption(id, 'logo'))} title={t('fullStory.video.remove')} className="p-1.5 rounded-lg text-gray-400 hover:text-danger-600 hover:bg-danger-50"><FiTrash2 size={14} /></button>
              )}
            </div>
          ) : <p className="text-xs text-gray-500">{t('fullStory.video.logoHint')}</p>}
          {!locked && (
            <>
              <button onClick={() => logoRef.current?.click()} className="btn btn-ghost text-xs py-1.5 px-2.5 flex items-center gap-1.5">
                <FiUpload size={12} /> {story.options.logo?.filename ? t('fullStory.video.replaceLogo') : t('fullStory.video.uploadLogo')}
              </button>
              <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) run(() => fullStoryService.uploadOption(id, 'logo', f)); }} />
            </>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 p-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-800"><FiMusic size={14} /> {t('fullStory.video.music')}</h3>
            {story.options.music?.filename && (
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={Boolean(story.options.music.enabled)}
                  disabled={locked}
                  onChange={(e) => run(() => fullStoryService.updateOptions(id, { music: { enabled: e.target.checked } }))}
                  className="w-4 h-4 rounded accent-primary-500"
                />
                {t('fullStory.video.useInVideo')}
              </label>
            )}
          </div>
          {story.options.music?.filename ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <p className="flex-1 min-w-0 text-xs text-gray-700 truncate">{story.options.music.originalName || t('fullStory.video.musicFile')}</p>
                {!locked && (
                  <button onClick={() => run(() => fullStoryService.removeOption(id, 'music'))} title={t('fullStory.video.remove')} className="p-1.5 rounded-lg text-gray-400 hover:text-danger-600 hover:bg-danger-50"><FiTrash2 size={14} /></button>
                )}
              </div>
              <audio controls preload="none" src={fullStoryService.fileUrl(id, story.options.music.filename)} className="w-full h-9" />
              <label className="block">
                <span className="flex justify-between text-[11px] text-gray-500">
                  <span>{t('fullStory.video.musicVolume')}</span>
                  <span className="tabular-nums">{Math.round((story.options.music.volume ?? 0.15) * 100)}%</span>
                </span>
                <input
                  type="range"
                  min="0"
                  max="0.6"
                  step="0.02"
                  defaultValue={story.options.music.volume ?? 0.15}
                  disabled={locked}
                  onMouseUp={(e) => run(() => fullStoryService.updateOptions(id, { music: { volume: Number(e.currentTarget.value) } }))}
                  onTouchEnd={(e) => run(() => fullStoryService.updateOptions(id, { music: { volume: Number(e.currentTarget.value) } }))}
                  onKeyUp={(e) => run(() => fullStoryService.updateOptions(id, { music: { volume: Number(e.currentTarget.value) } }))}
                  className="w-full accent-primary-500"
                />
              </label>
            </div>
          ) : <p className="text-xs text-gray-500">{t('fullStory.video.musicHint')}</p>}
          {!locked && (
            <>
              <button onClick={() => musicRef.current?.click()} className="btn btn-ghost text-xs py-1.5 px-2.5 flex items-center gap-1.5">
                <FiUpload size={12} /> {story.options.music?.filename ? t('fullStory.video.replaceMusic') : t('fullStory.video.uploadMusic')}
              </button>
              <input ref={musicRef} type="file" accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,audio/aac,audio/ogg" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) run(() => fullStoryService.uploadOption(id, 'music', f)); }} />
            </>
          )}
        </div>
      </div>

      {/* Render */}
      {story.video.status === 'failed' && <Notice tone="error">{story.video.error}</Notice>}
      {rendering ? (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div className="flex justify-between text-sm text-gray-700 mb-2">
            <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /> {t('fullStory.video.rendering')}</span>
            <span className="tabular-nums">{story.video.progress || 0}%</span>
          </div>
          <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
            <div className="h-full bg-primary-500 transition-all duration-700" style={{ width: `${story.video.progress || 0}%` }} />
          </div>
          <p className="text-xs text-gray-500 mt-2">{t('fullStory.video.renderingHint', { length: formatSeconds(story.voice.durationSec) })}</p>
        </div>
      ) : !readOnly && (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500">{t('fullStory.video.renderHint', { length: formatSeconds(story.voice.durationSec), scenes: story.scenes.length })}</p>
          <button
            onClick={() => run(() => fullStoryService.render(id))}
            disabled={!story.images.approved}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            <FiFilm size={15} /> {ready ? t('fullStory.video.remake') : t('fullStory.video.make')}
          </button>
        </div>
      )}
    </StepCard>
  );
};
